import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';

export const InstallPromptContext = React.createContext({
  canInstall: false,
  promptToInstall: async () => null,
});

const getIsStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const InstallPromptProvider = ({ children }) => {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => getIsStandalone());

  useEffect(() => {
    const displayModeMediaQuery = window.matchMedia(
      '(display-mode: standalone)',
    );

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    };

    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setIsInstalled(true);
    };

    const handleDisplayModeChange = (event) => {
      setIsInstalled(event.matches);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    if (displayModeMediaQuery.addEventListener) {
      displayModeMediaQuery.addEventListener('change', handleDisplayModeChange);
    } else {
      displayModeMediaQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('appinstalled', handleAppInstalled);

      if (displayModeMediaQuery.removeEventListener) {
        displayModeMediaQuery.removeEventListener(
          'change',
          handleDisplayModeChange,
        );
      } else {
        displayModeMediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const promptToInstall = useCallback(async () => {
    if (!installPromptEvent) {
      return null;
    }

    await installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;

    setInstallPromptEvent(null);

    return choice;
  }, [installPromptEvent]);

  const value = useMemo(
    () => ({
      canInstall: Boolean(installPromptEvent) && !isInstalled,
      promptToInstall,
    }),
    [installPromptEvent, isInstalled, promptToInstall],
  );

  return (
    <InstallPromptContext.Provider value={value}>
      {children}
    </InstallPromptContext.Provider>
  );
};

export const useInstallPrompt = () => useContext(InstallPromptContext);
